import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { createApiClient, reviewService, type ReviewDto } from '../../services/api';

interface ReviewsPanelProps {
  jobId: string;
  currentUserId: string;
  getToken: () => Promise<string>;
  canSubmit: boolean;
  counterpartyId: string;
  counterpartyName: string;
  revieweeLabel: string; // e.g. "Carrier", "Workshop", "Customer"
}

function Stars({ rating }: { rating: number }) {
  return (
    <span className="text-amber-400 text-sm leading-none">
      {'★'.repeat(rating)}
      <span className="text-gray-300">{'★'.repeat(5 - rating)}</span>
    </span>
  );
}

export function ReviewsPanel({
  jobId, currentUserId, getToken, canSubmit, counterpartyId, counterpartyName, revieweeLabel,
}: ReviewsPanelProps) {
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [showHistory, setShowHistory] = useState(false);

  const qc = useQueryClient();

  const { data: jobReviews = [] } = useQuery<ReviewDto[]>({
    queryKey: ['job-reviews', jobId],
    queryFn: () => reviewService(createApiClient(getToken)).getByJob(jobId),
    enabled: !!jobId,
    staleTime: 30_000,
  });

  const { data: counterpartyReviews = [], refetch: fetchCounterpartyReviews } = useQuery<ReviewDto[]>({
    queryKey: ['user-reviews', counterpartyId],
    queryFn: () => reviewService(createApiClient(getToken)).getByUser(counterpartyId),
    enabled: false,
    staleTime: 30_000,
  });

  const myReview = jobReviews.find(r => r.reviewerId === currentUserId);
  const theirReview = jobReviews.find(r => r.reviewerId === counterpartyId);

  const submitMutation = useMutation({
    mutationFn: () => reviewService(createApiClient(getToken)).submitReview({
      jobId, revieweeId: counterpartyId, rating, comment: comment.trim() || null,
    }),
    onSuccess: () => {
      setSubmitError(null);
      qc.invalidateQueries({ queryKey: ['job-reviews', jobId] });
    },
    onError: (err: any) =>
      setSubmitError(err.response?.data?.detail ?? err.response?.data?.error ?? 'Failed to submit review.'),
  });

  const handleToggleHistory = () => {
    if (!showHistory) fetchCounterpartyReviews();
    setShowHistory(v => !v);
  };

  if (!canSubmit && jobReviews.length === 0) return null;

  return (
    <div className="mt-6 bg-white border border-gray-200 rounded-xl p-5">
      <h2 className="font-semibold text-gray-900 mb-1">Reviews</h2>

      {/* Reviews already left on this job */}
      {jobReviews.length > 0 && (
        <div className="space-y-3 mb-4">
          {jobReviews.map(r => (
            <div key={r.id} className="border border-gray-100 rounded-lg p-3 bg-gray-50">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs font-medium text-gray-700">
                  {r.reviewerId === currentUserId ? 'You' : counterpartyName}
                </span>
                <Stars rating={r.rating} />
              </div>
              {r.comment && <p className="text-sm text-gray-600">{r.comment}</p>}
              <p className="text-xs text-gray-400 mt-1">
                {new Date(r.createdAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
              </p>
            </div>
          ))}
        </div>
      )}

      {/* Submit form — only while the current user hasn't already reviewed this job */}
      {canSubmit && !myReview && (
        <div className="border-t border-gray-100 pt-4">
          <p className="text-sm text-gray-500 mb-3">Rate the {revieweeLabel}</p>
          {submitError && (
            <p className="mb-3 text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{submitError}</p>
          )}
          <div className="flex gap-1 mb-4">
            {[1, 2, 3, 4, 5].map(star => (
              <button
                key={star}
                data-rating={star}
                onClick={() => setRating(star)}
                aria-label={`${star} star`}
                className={`text-2xl leading-none transition-colors ${star <= rating ? 'text-amber-400' : 'text-gray-300 hover:text-amber-300'}`}
              >
                ★
              </button>
            ))}
          </div>
          <textarea
            rows={3}
            placeholder="Comment (optional)"
            value={comment}
            onChange={e => setComment(e.target.value)}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 resize-none mb-3"
          />
          <button
            onClick={() => submitMutation.mutate()}
            disabled={submitMutation.isPending || rating === 0}
            className="bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white font-semibold px-6 py-2.5 rounded-lg transition-colors text-sm flex items-center gap-2"
          >
            {submitMutation.isPending && <span className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />}
            {submitMutation.isPending ? 'Submitting…' : 'Submit Review'}
          </button>
        </div>
      )}

      {myReview && !theirReview && (
        <p className="text-sm text-gray-500 border-t border-gray-100 pt-4">
          Waiting for {counterpartyName} to leave their review.
        </p>
      )}

      {/* Link to the counterparty's full review history across all their jobs */}
      {counterpartyId && (
        <div className="border-t border-gray-100 pt-3 mt-4">
          <button
            onClick={handleToggleHistory}
            className="text-sm text-blue-600 hover:text-blue-800 font-medium"
          >
            {showHistory ? 'Hide' : 'View'} all reviews for {counterpartyName}
          </button>
          {showHistory && (
            <div className="mt-3 space-y-2">
              {counterpartyReviews.length === 0 && (
                <p className="text-xs text-gray-400">No reviews yet.</p>
              )}
              {counterpartyReviews.map(r => (
                <div key={r.id} className="border border-gray-100 rounded-lg p-3">
                  <div className="flex items-center justify-between mb-1">
                    <Stars rating={r.rating} />
                    <span className="text-xs text-gray-400">
                      {new Date(r.createdAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                    </span>
                  </div>
                  {r.comment && <p className="text-sm text-gray-600">{r.comment}</p>}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
